import { useEffect } from 'react'
import { ARROWS, injectArrows } from './config'

export const useDatePickerNavigation = (
  datePickerRef: React.RefObject<HTMLDivElement | null>,
  value: Date | null,
  onChange: (date: Date, isValid: boolean) => void
) => {
  useEffect(() => {
    if (!datePickerRef.current) return

    const handleClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement

      if (target.classList.contains('arrow')) {
        e.stopPropagation()
        e.preventDefault()

        const arrow = ARROWS.find(a => target.classList.contains(a.class))
        if (arrow) {
          const newDate = new Date(value || new Date())
          arrow.action(newDate)
          onChange(newDate, false)
        }
      }
    }

    const handleMouseMove = (e: MouseEvent) => {
      const container = (e.target as HTMLElement).closest(
        '.--date-picker-curtain-month-info'
      )
      if (!container) return

      const rect = container.getBoundingClientRect()
      const x = e.clientX - rect.left
      const monthEl = container.querySelector(
        '.--date-picker-curtain-month-info-month'
      ) as HTMLElement
      const yearEl = container.querySelector(
        '.--date-picker-curtain-month-info-year'
      ) as HTMLElement

      if (!monthEl || !yearEl) return

      const monthRect = monthEl.getBoundingClientRect()
      const yearRect = yearEl.getBoundingClientRect()

      const isYearArrow =
        (x >= 8 && x <= 28) || (x >= rect.width - 28 && x <= rect.width - 8)
      const isMonthArrow =
        (x >= 36 && x <= 56) || (x >= rect.width - 56 && x <= rect.width - 36)
      const isOnMonthElement =
        x >= monthRect.left - rect.left && x <= monthRect.right - rect.left
      const isOnYearElement =
        x >= yearRect.left - rect.left && x <= yearRect.right - rect.left

      monthEl.style.backgroundColor =
        isMonthArrow || isOnMonthElement ? '#ccdce7' : ''
      yearEl.style.backgroundColor =
        isYearArrow || isOnYearElement ? '#ccdce7' : ''
    }

    const setupCurtain = (curtain: Element | null) => {
      if (!curtain) return null

      const monthInfo = curtain.querySelector(
        '.--date-picker-curtain-month-info'
      )
      if (monthInfo) injectArrows(monthInfo)

      curtain.addEventListener('click', handleClick as EventListener, true)
      curtain.addEventListener(
        'mousemove',
        handleMouseMove as EventListener,
        true
      )
      return curtain
    }

    const cleanupCurtain = (curtain: Element | null) => {
      if (!curtain) return
      curtain.removeEventListener('click', handleClick as EventListener, true)
      curtain.removeEventListener(
        'mousemove',
        handleMouseMove as EventListener,
        true
      )
    }

    let currentCurtain = setupCurtain(
      datePickerRef.current.querySelector('.--date-picker-curtain')
    )

    const observer = new MutationObserver(() => {
      const newCurtain = datePickerRef.current?.querySelector(
        '.--date-picker-curtain'
      )
      if (newCurtain && newCurtain !== currentCurtain) {
        cleanupCurtain(currentCurtain)
        currentCurtain = setupCurtain(newCurtain)
      }
    })

    observer.observe(datePickerRef.current, {
      childList: true,
      subtree: true,
    })

    return () => {
      observer.disconnect()
      cleanupCurtain(currentCurtain)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, onChange])
}
